"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { FallbackFrames } from "./FallbackFrames";
import { modeForPath, worldBus, type WorldMode } from "@/lib/world-bus";
import type { CaptureOptions } from "@/world";
import type { TierName } from "@/world/core/quality";
import styles from "./WorldRoot.module.css";

type WorldHandle = Awaited<ReturnType<typeof import("@/world").createWorld>>;

// Modo de captura (fotograma objetivo, capturas de las rondas, fotogramas de respaldo):
// ?captura=1&u=0&t=12&acumular=64&calidad=ultra&modo=home
function readCapture(): CaptureOptions | null {
  const q = new URLSearchParams(window.location.search);
  if (!q.has("captura")) return null;
  const tier = (q.get("calidad") ?? "high") as TierName;
  return {
    u: Number(q.get("u") ?? 0),
    time: Number(q.get("t") ?? 12),
    accumulate: Math.max(1, Number(q.get("acumular") ?? 1)),
    tier,
    mode: captureMode(q.get("modo")),
    noWord: q.has("sinpalabra"),
  };
}

const CAPTURE_MODES: Record<string, WorldMode> = {
  fondo: "background",
  investigacion: "research",
  modelos: "models",
  auditoria: "audit",
  quieto: "still",
  perdido: "lost",
};

// Sin "modo", la escena de la ruta que se captura (el inicio, si es "/").
function captureMode(v: string | null): WorldMode {
  if (v && CAPTURE_MODES[v]) return CAPTURE_MODES[v];
  return modeForPath(window.location.pathname);
}

/** Lo que se sabe del equipo antes de arrancar: si hay WebGL2 usable y qué GPU es. */
function probeWebGL2(): { ok: boolean; renderer: string } {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl2");
    const ok = !!gl && !!gl.getExtension("EXT_color_buffer_float");
    let renderer = "";
    if (gl) {
      const info = gl.getExtension("WEBGL_debug_renderer_info");
      renderer = String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
    }
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
    return { ok, renderer };
  } catch {
    return { ok: false, renderer: "" };
  }
}

// Si en esta GPU la escena ya falló (shader, negro, contexto perdido, no arrancó), la
// próxima visita va directo a los fotogramas: no se cobra otra vez la descarga y la espera.
// Se sube el número cada vez que se corrige una causa de falla: los equipos marcados por
// una versión vieja vuelven a probar. v3: enteros highp en los chequeos de NaN (Mali). El
// Poco X7 Pro quedó marcado con la v2 y por eso seguía en respaldo sin ?diag.
const BROKEN_KEY = "yvyrai:mundo-respaldo:v3";
const OLD_KEYS = ["yvyrai:mundo-respaldo", "yvyrai:mundo-respaldo:v2"];
const BROKEN_DAYS = 14;

function knownBroken(renderer: string) {
  try {
    OLD_KEYS.forEach((k) => localStorage.removeItem(k));
    const v = JSON.parse(localStorage.getItem(BROKEN_KEY) ?? "null") as { r: string; t: number } | null;
    return !!v && v.r === renderer && Date.now() - v.t < BROKEN_DAYS * 864e5;
  } catch {
    return false;
  }
}

function rememberBroken(renderer: string) {
  try {
    localStorage.setItem(BROKEN_KEY, JSON.stringify({ r: renderer, t: Date.now() }));
  } catch {
    /* modo privado: no pasa nada */
  }
}

/** Si el mundo no está listo en este tiempo, se queda el respaldo (Android lento o colgado). */
const BOOT_TIMEOUT_MS = 15000;
/** Cuánto se espera al 3D antes de poner los fotogramas, para no ver un fondo vacío. */
const FRAMES_AFTER_MS = 1200;

export function WorldRoot() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const worldRef = useRef<WorldHandle | null>(null);
  const pathname = usePathname();
  // Fotogramas mientras el 3D tarda: nunca un fondo vacío (lo que pasaba en Android).
  const [framesEarly, setFramesEarly] = useState(false);
  const status = useSyncExternalStore(
    (fn) => worldBus.subscribe(fn),
    () => worldBus.status,
    () => "idle",
  );

  // El modo del mundo sigue a la ruta: cada página tiene su escena (guía 7.4). La 404
  // pide la suya desde su propio componente.
  useEffect(() => {
    worldBus.setMode(modeForPath(pathname));
  }, [pathname]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let disposed = false;
    const capture = readCapture();
    const q = new URLSearchParams(window.location.search);
    const forcedTier = (q.get("calidad") as TierName | null) ?? null;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const diag = q.has("diag");

    const probe = probeWebGL2();
    if (diag) worldBus.diag = `gpu: ${probe.renderer || "?"} · webgl2+float: ${probe.ok ? "si" : "NO"}`;
    if (!probe.ok || (!capture && !diag && knownBroken(probe.renderer))) {
      worldBus.setStatus("fallback");
      return;
    }
    worldBus.setStatus("loading");
    const framesTimer = capture ? 0 : window.setTimeout(() => setFramesEarly(true), FRAMES_AFTER_MS);
    let bootTimer = 0;
    // Si el mundo cae al respaldo por cualquier motivo, se recuerda para esta GPU.
    const unsubscribe = worldBus.subscribe(() => {
      if (worldBus.status === "fallback" && !capture && !diag) {
        rememberBroken(probe.renderer);
        window.clearTimeout(bootTimer);
      }
    });

    const boot = async () => {
      if (!capture)
        bootTimer = window.setTimeout(() => {
          if (worldBus.status === "loading") {
            console.warn("[mundo] respaldo: la escena no arrancó a tiempo");
            worldBus.brokenReason = "tiempo de arranque";
            worldBus.setStatus("fallback");
          }
        }, BOOT_TIMEOUT_MS);
      try {
        const mod = await import("@/world");
        if (disposed) return;
        if (process.env.NODE_ENV === "development" && q.has("look")) {
          const [{ applyLookOverrides }, { look }] = await Promise.all([
            import("@/world/dev/look-override"),
            import("@/world/look"),
          ]);
          console.info("[mundo] look desde la URL:", applyLookOverrides(look, q.get("look") ?? ""));
        }
        const world = await mod.createWorld(canvas, { reducedMotion: reduced.matches, capture, forcedTier });
        // Llegó tarde (el reloj de arranque ya puso el respaldo): no se muestra.
        if (disposed || worldBus.status === "fallback") {
          world.dispose();
          return;
        }
        worldRef.current = world;
        world.start();
        if (process.env.NODE_ENV === "development" && q.has("panel")) {
          const { mountInspector } = await import("@/world/dev/inspector");
          mountInspector(world);
        }
        (window as unknown as { __world?: WorldHandle }).__world = world;
      } catch (err) {
        console.error("[mundo] no se pudo iniciar la escena", err);
        worldRef.current?.dispose();
        worldRef.current = null;
        worldBus.setStatus("fallback");
      }
    };

    // El 3D entra después de que el texto ya se pintó (guía 7.8). Safari no tiene
    // requestIdleCallback: ahí alcanza con un temporizador corto.
    const hasIdle = typeof window.requestIdleCallback === "function";
    let id = 0;
    const schedule = () => {
      id = hasIdle ? window.requestIdleCallback(() => void boot(), { timeout: 600 }) : window.setTimeout(() => void boot(), 120);
    };
    // En el inicio el 3D es la experiencia: arranca enseguida. En las páginas internas
    // es un fondo lejano y quieto: espera a que la página termine de cargar y a que la
    // persona empiece a leer (o a interactuar), para no competir con la lectura ni con
    // la hidratación en un celular de gama media.
    const wake = ["pointermove", "pointerdown", "scroll", "keydown", "touchstart"] as const;
    let lateTimer = 0;
    const startLate = () => {
      wake.forEach((ev) => window.removeEventListener(ev, startLate));
      window.clearTimeout(lateTimer);
      if (!disposed && worldBus.status === "loading") schedule();
    };
    const deferred = window.location.pathname !== "/" && !capture;
    if (!deferred) schedule();
    else {
      const arm = () => {
        lateTimer = window.setTimeout(startLate, 2500);
        wake.forEach((ev) => window.addEventListener(ev, startLate, { passive: true, once: true }));
      };
      if (document.readyState === "complete") arm();
      else window.addEventListener("load", arm, { once: true });
    }

    const onResize = () => worldRef.current?.resize();
    const onVisibility = () => {
      const w = worldRef.current;
      if (!w) return;
      if (document.hidden) w.stop();
      else w.start();
    };
    const onPointer = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      worldBus.pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      worldBus.pointer.y = -((e.clientY / window.innerHeight) * 2 - 1);
    };
    const onReduced = () => {
      const w = worldRef.current;
      if (w) w.director.reducedMotion = reduced.matches;
    };
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pointermove", onPointer, { passive: true });
    reduced.addEventListener("change", onReduced);

    return () => {
      disposed = true;
      unsubscribe();
      window.clearTimeout(framesTimer);
      window.clearTimeout(bootTimer);
      if (hasIdle) window.cancelIdleCallback(id);
      else window.clearTimeout(id);
      window.clearTimeout(lateTimer);
      wake.forEach((ev) => window.removeEventListener(ev, startLate));
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pointermove", onPointer);
      reduced.removeEventListener("change", onReduced);
      worldRef.current?.dispose();
      worldRef.current = null;
    };
  }, []);

  // Con el 3D listo, los fotogramas tempranos se quedan hasta que termina el fundido y
  // después se desmontan (no siguen decodificando imágenes debajo del canvas).
  const [framesGone, setFramesGone] = useState(false);
  useEffect(() => {
    if (status !== "ready" || !framesEarly) return;
    const t = window.setTimeout(() => setFramesGone(true), 2000);
    return () => window.clearTimeout(t);
  }, [status, framesEarly]);
  const showFrames = status === "fallback" || (framesEarly && !framesGone && status !== "idle");
  const diagText = useDiag(status);

  return (
    <div className={styles.world} aria-hidden="true" data-frames={framesEarly ? "" : undefined}>
      <div className={styles.backdrop} />
      {showFrames && <FallbackFrames />}
      <canvas ref={canvasRef} className={styles.canvas} />
      {diagText !== null && (
        <pre className={styles.diag}>
          {`estado: ${status}${worldBus.brokenReason ? ` (${worldBus.brokenReason})` : ""}\n${diagText}`}
        </pre>
      )}
    </div>
  );
}

/**
 * Diagnóstico en pantalla con ?diag en la URL: qué GPU es, qué extensiones tiene, qué dio
 * el autotest y qué hay en cada etapa interna del render. Para sacarle una captura en el
 * celular que falla, sin consola de desarrollador.
 */
function useDiag(status: string): string | null {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("diag")) return;
    const tick = () => setText(worldBus.diag || "…");
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [status]);
  return text;
}
